import { useLayoutEffect } from "react";
import { Navigate } from "react-router-dom";
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { MotionConfig } from "framer-motion";
import Dashboard from './components/Dashboard';
import StudyDetailsPage from './components/StudyDetailsPage';
import EditStudyPage from './components/EditStudyPage';
import LoginPage from './components/LoginPage';
import SetPasswordPage from './components/SetPasswordPage';
import HubReturn from './pages/HubReturn';
import ChangePasswordPage from './components/ChangePasswordPage';
import RequireAuth from './components/RequireAuth';
import EmployeeCredentialsPage from './components/EmployeeCredentialsPage';
import EmployeeDetailPage from './components/EmployeeDetailPage';
import AddEmployeePage from './components/AddEmployeePage';
import NotificationsPage from './components/NotificationsPage';
import CoordinatorPage from './components/CoordinatorPage';
import ReceptionPage from './components/ReceptionPage';
import ReferralPage from './components/ReferralPage';
import RawDatabasePage from './components/RawDatabasePage';
import RawDatabaseQueryPage from './components/RawDatabaseQueryPage';
import PatientDetailPage from './components/PatientDetailPage';
import StatisticsPage from "./components/StatisticsPage";
import WeeklySurvey from "./components/WeeklySurvey";
import AdminReportsPage from "./components/AdminReportsPage";
import AIChatPage from './components/AIChatPage';
import ChatLauncher from './components/ChatLauncher'; // ✅ new import
import LightModeToggle from './components/LightModeToggle';
import EBinderPage from './components/EBinderPage';
import MonitorsPage from "./components/MonitorsPage";
import AdminMonitors from "./components/AdminMonitors";
import MonitorViewPage from "./components/MonitorViewPage";
import ESignPage from "./components/ESignPage";
import ReferralQuestionnairePage from "./components/ReferralQuestionnairePage";
import SourceXHome from "./pages/SourceXHome";
import PatientSource from "./pages/PatientSource";
import SectionEditor from "./pages/SectionEditor"; // ✅ Add this import
import ProtocolsPage from "./pages/ProtocolsPage";
import EcrfPage from "./pages/EcrfPage";
import JoinReviewPage from "./pages/JoinReviewPage";
import ProtocolReviewPage from "./pages/ProtocolReviewPage";
import PftUploadPage from "./pages/PftUploadPage";
import PftImportsPage from "./pages/PftImportsPage";
import DuplicateScanPage from "./pages/DuplicateScanPage";
import {
  PROTOCOL_EXTRACTION_ENABLED,
  ECRF_EXTRACTION_ENABLED,
  JOIN_REVIEW_ENABLED,
  PFT_UPLOAD_ENABLED,
  PFT_IMPORT_ENABLED,
  PFT_SURVEY_TEXT_ENABLED,
  VAULTX_COMING_SOON,
  HUB_SIGNIN_HIDDEN,
} from "./config/featureFlags";
import { UI_REVAMP_ENABLED } from "./config/featureFlags";
import HomePage from "./pages/HomePage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import VaultXComingSoon from "./components/VaultXComingSoon";
import StudyPatientsPage from "./components/StudyPatientsPage";
import Studies from "./components/Studies";
import StudyContactsPage from "./components/StudyContactsPage";
import PatientQueryPage from "./components/PatientQueryPage";
import ManageCoordinatorsPage from "./components/ManageCoordinatorsPage";
import TenantHeader from "./components/TenantHeader";
import ReportReminder from "./components/ReportReminder";
import OrgManagementPage from "./components/OrgManagementPage";
import ResearchCalendarPage from "./components/ResearchCalendarPage";
import { useTenant } from "./context/TenantContext";
import { applyRevampTheme } from "./utils/revampTheme";


function AppShell() {
  // Re-key the routed content on the active tenant/site so a site switch fully
  // remounts pages and re-fetches their data against the new path.
  //
  // EXCEPTION: public/auth routes (login, root, e-sign) use a STABLE key. They
  // don't depend on site data, and re-keying them would remount LoginPage if
  // the active site changes mid-login (which it does when login restores your
  // primary site), silently dropping the OTP step. /hub-return likewise sets
  // the hub's site mid-sign-in, and its one-time pass can't be read twice.
  const { tenantId, siteId } = useTenant();
  const location = useLocation();
  const path = location.pathname;
  const isPublic =
    path === "/" ||
    path === "/login" ||
    path === "/hub-return" ||
    path === "/set-password" ||
    path.startsWith("/esign") ||
    path.startsWith("/referral-form") ||
    path.startsWith("/health-survey");
  const contentKey = isPublic ? "public" : `${tenantId}:${siteId}`;
  // Patient-facing pages keep their original look, whichever way they're reached.
  useLayoutEffect(() => {
    applyRevampTheme(path);
  }, [path]);
  return (
    <div key={contentKey}>
        <Routes>
          {/* Shared Login Page */}
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/login" element={<LoginPage />} />
          {/* Signing in through Xiva Login (the hub's one-time pass). Not set up
              on production yet (HUB_SIGNIN_HIDDEN): straight to the normal login. */}
          <Route
            path="/hub-return"
            element={HUB_SIGNIN_HIDDEN ? <Navigate to="/login" replace /> : <HubReturn />}
          />

          {/* Self-service password set (public — uses an emailed oobCode) */}
          <Route path="/set-password" element={<SetPasswordPage />} />
          {/* Password change (signed-in; also the forced-at-90-days target) */}
          <Route
            path="/change-password"
            element={
              <RequireAuth>
                <ChangePasswordPage />
              </RequireAuth>
            }
          />

           <Route
        path="/sourcex"
        element={
        <RequireAuth roles={["admin", "coordinator", "referral"]}>
        <SourceXHome />
        </RequireAuth>
        }
        />
        <Route
        path="/sourcex/patient/:id"
        element={
        <RequireAuth roles={["admin", "coordinator", "referral"]}>
        <PatientSource />
        </RequireAuth>
        }
        />
        <Route
  path="/sourcex/template/:studyId"
  element={
    <RequireAuth roles={["admin", "coordinator", "referral"]}>
      <SectionEditor />
    </RequireAuth>
  }
/>

{/* Protocol → SOA extraction pipeline (Phase 9) — feature-flagged */}
{PROTOCOL_EXTRACTION_ENABLED && (
  <Route
    path="/sourcex/protocols/:studyId"
    element={
      <RequireAuth roles={["admin", "coordinator", "referral"]}>
        <ProtocolsPage />
      </RequireAuth>
    }
  />
)}
{PROTOCOL_EXTRACTION_ENABLED && (
  <Route
    path="/sourcex/protocols/:studyId/:protocolId"
    element={
      <RequireAuth roles={["admin", "coordinator", "referral"]}>
        <ProtocolReviewPage />
      </RequireAuth>
    }
  />
)}
{/* Blank eCRF → forms/fields/branching (Phase 1b) — separately flagged */}
{ECRF_EXTRACTION_ENABLED && (
  <Route
    path="/sourcex/ecrf/:studyId"
    element={
      <RequireAuth roles={["admin", "coordinator"]}>
        <EcrfPage />
      </RequireAuth>
    }
  />
)}
{/* SoA x eCRF join review (Phase 2) — needs BOTH ingestion phases to have run */}
{JOIN_REVIEW_ENABLED && (
  <Route
    path="/sourcex/join/:studyId"
    element={
      <RequireAuth roles={["admin", "coordinator"]}>
        <JoinReviewPage />
      </RequireAuth>
    }
  />
)}


          {/* UI revamp: the personal home page. Same sign-in + OTP guard as
              every page; the roles that land on it (utils/roleHome.js): admins
              (owners pass) and coordinators. Reception and referral stay on
              their own pages (user, 2026-11-02). */}
          {UI_REVAMP_ENABLED && (
            <Route
              path="/home"
              element={
                <RequireAuth roles={["admin", "coordinator"]}>
                  <HomePage />
                </RequireAuth>
              }
            />
          )}
          {/* UI revamp: admins post / remove home page announcements. Who may
              post to which audience is enforced by firestore.rules. */}
          {UI_REVAMP_ENABLED && (
            <Route
              path="/announcements"
              element={
                <RequireAuth roles={["admin"]}>
                  <AnnouncementsPage />
                </RequireAuth>
              }
            />
          )}
          {/* Admin Portal (Protected) */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth roles={["admin"]}>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/study/:id"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <StudyDetailsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/edit/:id"
            element={
              <RequireAuth roles={["admin"]}>
                <EditStudyPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials"
            element={
              <RequireAuth roles={["admin"]}>
                <EmployeeCredentialsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/employee/:id"
            element={
              <RequireAuth roles={["admin"]}>
                <EmployeeDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/employee/add"
            element={
              <RequireAuth roles={["admin"]}>
                <EmployeeDetailPage isNew />
              </RequireAuth>
            }
          />
          <Route
            path="/add-employee"
            element={
              <RequireAuth roles={["admin"]}>
                <AddEmployeePage />
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth roles={["admin"]}>
                <NotificationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/monitors"
            element={
              <RequireAuth roles={["admin"]}>
                <AdminMonitors />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/coordinators"
            element={
              <RequireAuth roles={["admin"]}>
                <ManageCoordinatorsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage"
            element={
              <RequireAuth roles={["admin"]}>
                <OrgManagementPage />
              </RequireAuth>
            }
          />
          {/* Shared Research Google Calendar view — room + coordinator scheduling */}
          <Route
            path="/research-calendar"
            element={
              <RequireAuth roles={["admin", "coordinator"]}>
                <ResearchCalendarPage />
              </RequireAuth>
            }
          />

          {/* Provider Portal — REMOVED (D-17). It was never openable by anyone:
              the page gated on a `providerAuth` browser flag and sent everyone
              else to /provider-login, a route that was never wired up, so every
              visit rendered blank in both environments. Its login screen was
              never imported, and hardcoded a username and password in source.
              The pages and that login screen are deleted; recover them from git
              history if the portal is ever wanted, but rebuild it behind a real
              account with a `provider` role rather than a browser flag.

              These redirects are deliberately KEPT, unconditionally: links to
              /provider may still be bookmarked or sitting in an old email, and
              a redirect to /login is a better answer than a blank page. */}
          <Route path="/provider" element={<Navigate to="/login" replace />} />
          <Route
            path="/provider/study/:id"
            element={<Navigate to="/login" replace />}
          />

          {/* Coordinator View */}
          <Route
            path="/coordinator"
            element={
              <RequireAuth roles={["admin", "coordinator", "reception", "referral"]}>
                <CoordinatorPage />
              </RequireAuth>
            }
          />
          <Route
            path="/statistics"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <StatisticsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/coordinator/patient/:id"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <PatientDetailPage />
              </RequireAuth>
            }
          />
          {/* Raw Database — referral submissions call-duty page */}
          <Route
            path="/coordinator/raw-database"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <RawDatabasePage />
              </RequireAuth>
            }
          />
          {PFT_IMPORT_ENABLED && (
            <Route
              path="/coordinator/pft-uploads"
              element={
                <RequireAuth roles={["admin", "coordinator"]}>
                  <PftImportsPage />
                </RequireAuth>
              }
            />
          )}
          {/* Possible duplicate patients: on screen only, no download (opened in a new tab from the CRC page) */}
          <Route
            path="/coordinator/duplicate-scan"
            element={
              <RequireAuth roles={["admin", "coordinator"]}>
                <DuplicateScanPage />
              </RequireAuth>
            }
          />
          {/* Raw Database — query + CSV export */}
          <Route
            path="/coordinator/raw-database/query"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <RawDatabaseQueryPage />
              </RequireAuth>
            }
          />
          <Route
            path="/studies"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <Studies />
              </RequireAuth>
            }
          />
          <Route
            path="/contacts"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <StudyContactsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/query"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <PatientQueryPage />
              </RequireAuth>
            }
          />

          {/* Weekly Reports */}
          <Route
            path="/weekly-survey"
            element={
              <RequireAuth roles={["admin", "coordinator", "referral"]}>
                <WeeklySurvey />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/weekly-reports"
            element={
              <RequireAuth roles={["admin"]}>
                <AdminReportsPage />
              </RequireAuth>
            }
          />
          {PFT_UPLOAD_ENABLED && (
            <Route
              path="/admin/pft-upload"
              element={
                <RequireAuth roles={["admin"]}>
                  <PftUploadPage />
                </RequireAuth>
              }
            />
          )}
          {/* The AI assistant is KEPT, but is no longer reachable without a
              login. Nothing in the app links to it (the "Chat with AI" launcher
              points at Xiva, a separate external portal), yet it was public and
              POSTed free text — meant to be a patient note — to chatWithAI with
              no auth header at all. So a page nobody navigated to was an open
              PHI path. Gated on authentication + 2FA only: there is no role
              list because there is no nav entry saying who it is for. */}
          <Route
            path="/ai-assistant"
            element={
              <RequireAuth>
                <AIChatPage />
              </RequireAuth>
            }
          />

          {/* eBinder and the monitor list were reachable with no login at all.
              Gated on authentication + 2FA only (no `roles`), because /ebinder is
              entered from BOTH the admin Dashboard and the coordinator page — a
              role list here would lock one of them out. On production it shows
              "VaultX is coming soon" instead (VAULTX_COMING_SOON). */}
          <Route
            path="/ebinder"
            element={
              <RequireAuth>
                {VAULTX_COMING_SOON ? <VaultXComingSoon /> : <EBinderPage />}
              </RequireAuth>
            }
          />

          {/* Reception View */}
          <Route
            path="/reception"
            element={
              <RequireAuth roles={["admin", "reception"]}>
                <ReceptionPage />
              </RequireAuth>
            }
          />
          {/* Referral View — phone-number mass text + study library only */}
          <Route
            path="/referral"
            element={
              <RequireAuth roles={["admin", "referral"]}>
                <ReferralPage />
              </RequireAuth>
            }
          />
          <Route
            path="/monitors"
            element={
              <RequireAuth>
                <MonitorsPage />
              </RequireAuth>
            }
          />
          {/* Already required auth.currentUser internally and returned early
              without it — gating the route just stops it claiming to be public. */}
          <Route
            path="/monitor-view/:requestId"
            element={
              <RequireAuth>
                <MonitorViewPage />
              </RequireAuth>
            }
          />

          {/* E-sign consent (D-16). The siteId form is what Reception mints now:
              the patient's phone has no active site, so without it the token is
              looked up at the default site and a link issued anywhere else is
              never found. The site-less route is KEPT for links already texted,
              and falls back to the default site inside ESignPage. Same shape as
              /referral-form below. */}
          <Route path="/esign/:siteId/:token" element={<ESignPage />} />
          <Route path="/esign/:token" element={<ESignPage />} />
          {/* Site-scoped referral link (new). The legacy site-less link below
              still works and resolves to the default (Dubois) site. */}
          <Route path="/referral-form/:siteId/:token" element={<ReferralQuestionnairePage />} />
          <Route path="/referral-form/:token" element={<ReferralQuestionnairePage />} />
          {/* PFT Uploads health survey (flag PFT_SURVEY_TEXT_ENABLED): the same form and
              consent PDF, with its own link and a server-side lookup / submit. */}
          {PFT_SURVEY_TEXT_ENABLED && (
            <Route path="/health-survey/:siteId/:token" element={<ReferralQuestionnairePage mode="pft" />} />
          )}
          {/* Admin access — editable */}
<Route
  path="/admin/study-patients"
  element={
    <RequireAuth roles={["admin"]}>
      <StudyPatientsPage />
    </RequireAuth>
  }
/>

{/* Coordinator access — view-only */}
<Route
  path="/coordinator/study-patients"
  element={
    <RequireAuth roles={["admin", "coordinator", "referral"]}>
      <StudyPatientsPage />
    </RequireAuth>
  }
/>

        </Routes>

        {/* ✅ New "Chat with AI" launcher on all pages except /ai-assistant */}
        {/* UI revamp: hidden for now (user, 2026-10-30). */}
        {!UI_REVAMP_ENABLED && <ChatLauncher />}
        {/* Referral questionnaire is patient-facing and locked to light mode —
            don't mount the toggle there so it can't fight the forced class. */}
        {!path.startsWith("/referral-form") && !path.startsWith("/health-survey") && (
          <LightModeToggle
            hidden={UI_REVAMP_ENABLED && ["/dashboard", "/coordinator", "/reception", "/home"].includes(path)}
          />
        )}
        {/* Global company/site indicator + switcher (hidden on public pages) */}
        <TenantHeader />
        {/* Friday / month-end "submit your weekly report" pop-up (coordinators only) */}
        <ReportReminder />
    </div>
  );
}

function App() {
  return (
    <Router>
      <MotionConfig reducedMotion="user">
        <AppShell />
      </MotionConfig>
    </Router>
  );
}

export default App;
